import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getNewsletterSubscribers,
  deleteNewsletterSubscriber,
} from '../../services/firebaseService';
import toast from 'react-hot-toast';

export default function AdminNewsletter() {
  const { t } = useTranslation();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchSubscribers();
  }, []);

  async function fetchSubscribers() {
    try {
      const data = await getNewsletterSubscribers();
      setSubscribers(data);
    } catch {
      toast.error(t('admin.failedLoad'));
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id, email) {
    if (!window.confirm(t('admin.removeSubscriberConfirm', { email }))) return;
    try {
      await deleteNewsletterSubscriber(id);
      setSubscribers(prev => prev.filter(s => s.id !== id));
      toast.success(t('admin.subscriberRemoved'));
    } catch {
      toast.error(t('admin.failedDelete'));
    }
  }

  function sanitizeCsvCell(value) {
    if (typeof value !== 'string') return value;
    // Prevent CSV injection: prefix dangerous chars with a single quote
    if (/^[=+\-@\t\r]/.test(value)) return `'${value}`;
    return value;
  }

  function handleExport() {
    const csvContent = 'Email,Date\n' + subscribers.map(s => {
      const date = s.createdAt?.toDate ? s.createdAt.toDate().toISOString() : '';
      return `${sanitizeCsvCell(s.email)},${sanitizeCsvCell(date)}`;
    }).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-subscribers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function formatDate(timestamp) {
    if (!timestamp) return '—';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  }

  const filtered = subscribers.filter(s =>
    s.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="admin-loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="admin-newsletter">
      <div className="admin-page-header">
        <div>
          <h1>{t('admin.newsletter')}</h1>
          <p>{subscribers.length} {t('admin.totalSubscribers')}</p>
        </div>
        {subscribers.length > 0 && (
          <button onClick={handleExport} className="btn btn-secondary">
            <i className="fas fa-download"></i> {t('admin.exportCsv')}
          </button>
        )}
      </div>

      {subscribers.length > 0 && (
        <div className="admin-toolbar">
          <div className="admin-search">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder={t('admin.searchSubscribers')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="admin-empty">
          <i className="fas fa-users"></i>
          <p>{search ? t('admin.noMatchingSubscribers') : t('admin.noSubscribersYet')}</p>
        </div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{t('admin.email')}</th>
                <th>{t('admin.date')}</th>
                <th>{t('admin.status')}</th>
                <th>{t('admin.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((sub, i) => (
                <tr key={sub.id}>
                  <td>{i + 1}</td>
                  <td>{sub.email}</td>
                  <td>{formatDate(sub.createdAt)}</td>
                  <td>
                    <span className={`admin-badge ${sub.isActive ? 'success' : 'muted'}`}>
                      {sub.isActive ? t('admin.active') : t('admin.inactive')}
                    </span>
                  </td>
                  <td>
                    <div className="admin-actions">
                      <button onClick={() => handleDelete(sub.id, sub.email)} className="admin-action-btn delete" title={t('admin.failedDelete')}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
